import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../services/api.js";
import { Users } from "lucide-react";
import SyncPppSecretByUsermanId from "../components/Customer/Ppp/SyncPppSecretByUsermanId.js";
import PppMenu from "../components/Customer/Ppp/PppMenu.js";
import EnableDisablePppModal from "../components/Customer/Ppp/EnableDisablePppModal.js";
import AddWfhPppSecretModal from "../components/PppSecret/AddPppSecretModal.js";
import EditPppSecretModal from "../components/PppSecret/EditPppSecretModal.js";
import DeletePppSecretModal from "../components/PppSecret/DeletePppSecretModal.js";

const PppSecrets = () => {
  const [searchParams] = useSearchParams();
  const usermanId = searchParams.get("userman_id");
  const deviceName = searchParams.get("device_name");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const limit = 1000;
  const [totalPages, setTotalPages] = useState(1);
  const [totalBackend, setTotalBackend] = useState(0);

  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  // SERVER SIDE FILTER
  const [filters, setFilters] = useState({
    name: "",
    bill_status: "",
    customer_id: "",
  });

  // CLIENT SIDE QUICK SEARCH
  const [quickSearch, setQuickSearch] = useState("");

  const fetchPppSecrets = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");

      const res = await api.get(`/ppp-secret-by-device/${usermanId}`, {
        headers: { Authorization: token },
        params: {
          page,
          limit,
          name: filters.name || undefined,
          disabled: filters.disabled || undefined,
          bill_status: filters.bill_status || undefined,
          cust_status: filters.cust_status || undefined,
          customer_id: filters.customer_id || undefined,
        },
      });

      setData(res.data.data);
      setTotalPages(res.data.pagination.totalPages);
      setTotalBackend(res.data.pagination.total);
    } catch (err) {
      console.error(err);
      setError("Gagal mengambil data PPP Secret");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (usermanId) fetchPppSecrets();
  }, [usermanId, page]);

  const handleChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleSearch = () => {
    setPage(1);
    fetchPppSecrets();
  };

  // QUICK SEARCH (CLIENT SIDE)
  const filteredData = data.filter((item) =>
    [item.name, item.profile, item.comment]
      .join(" ")
      .toLowerCase()
      .includes(quickSearch.toLowerCase())
  );

  const handleOpenModal = (row) => {
    setSelectedRow(row);
    setOpenModal(true);
  };

  const handleOpenEditModal = (row) => {
    setSelectedRow(row);
    setOpenEditModal(true);
  };

  const handleOpenDeleteModal = (row) => {
    setSelectedRow(row);
    setOpenDeleteModal(true);
  };

  return (
    <div className="p-6 dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-200">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users className="w-6 h-6" />
          {deviceName} /{" "}
          <PppMenu userman_id={usermanId} device_name={deviceName} /> / Secrets
        </h1>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setOpenAddModal(true)}
            className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
          >
            Activation
          </button>

          <SyncPppSecretByUsermanId
            usermanId={usermanId}
            onSuccess={() => {
              setPage(1);
              fetchPppSecrets();
            }}
          />
        </div>
      </div>

      {/* FILTER SECTION */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
        <input
          type="text"
          name="name"
          placeholder="Search name"
          value={filters.name}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <select
          name="disabled"
          value={filters.disabled}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">Any</option>
          <option value="0">enabled</option>
          <option value="1">disabled</option>
        </select>

        <select
          name="bill_status"
          value={filters.bill_status}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">All Bill Status</option>
          <option value="paid">paid</option>
          <option value="unpaid">unpaid</option>
          <option value="not found">not found</option>
        </select>

        <select
          name="cust_status"
          value={filters.cust_status}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        >
          <option value="">All Customer Status</option>
          <option value="active">active</option>
          <option value="not found">not found</option>
          <option value="closed">closed</option>
          <option value="Unknown">unknown</option>
        </select>

        <input
          type="text"
          name="customer_id"
          placeholder="Customer ID"
          value={filters.customer_id}
          onChange={handleChange}
          className="px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <button
          onClick={handleSearch}
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Search
        </button>
      </div>

      {/* QUICK SEARCH */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <input
          type="text"
          placeholder="Quick search (name / profile / comment)"
          value={quickSearch}
          onChange={(e) => {
            setPage(1);
            setQuickSearch(e.target.value);
          }}
          className="w-full max-w-sm px-3 py-2 rounded border dark:border-slate-700 dark:bg-slate-800"
        />

        <div className="flex gap-4 text-sm">
          <span>
            Total MikroTik: <b>{totalBackend}</b>
          </span>
          <span>
            Showing: <b>{filteredData.length}</b>
          </span>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto rounded border dark:border-slate-700">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2 text-left">Mikrotik ID</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Profile</th>
              <th className="px-4 py-2 text-left">Comment</th>
              <th className="px-4 py-2 text-left">Admin Note</th>
              <th className="px-4 py-2 text-center">Bill</th>
              <th className="px-4 py-2 text-center">Customer Status</th>
              <th className="px-4 py-2 text-center">Disabled</th>
              <th className="px-4 py-2 text-center">Updated at</th>
              <th className="px-4 py-2 text-center"></th>
              <th className="px-4 py-2 text-center"></th>
              <th className="px-4 py-2 text-center"></th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-red-500">
                  {error}
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center">
                  Tidak ada data
                </td>
              </tr>
            ) : (
              filteredData.map((row) => (
                <tr
                  key={row.id}
                  className="border-t dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <td className="px-4 py-2">{row.mikrotik_id}</td>
                  <td className="px-4 py-2">{row.name}</td>
                  <td className="px-4 py-2">{row.profile || "-"}</td>
                  <td className="px-4 py-2">{row.comment || "-"}</td>
                  <td className="px-4 py-2">{row.admin_note || "-"}</td>
                  <td className="px-4 py-2 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        row.bill_status === "paid"
                          ? "bg-green-100 text-green-700"
                          : row.bill_status === "unpaid"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {row.bill_status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        row.cust_status === "active"
                          ? "bg-green-100 text-green-700"
                          : row.cust_status === "Unknown"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {row.cust_status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {row.disabled ? (
                      <span className="text-red-500">Yes</span>
                    ) : (
                      <span className="text-green-500">No</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {new Date(row.updated_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => handleOpenEditModal(row)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 text-xs rounded"
                    >
                      Edit
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => handleOpenModal(row)}
                      className={`px-2 py-1 text-xs rounded text-white
      ${
        row.disabled
          ? "bg-green-600 hover:bg-green-700"
          : "bg-orange-600 hover:bg-orange-700"
      }
    `}
                    >
                      {row.disabled ? "Enable" : "Disable"}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => handleOpenDeleteModal(row)}
                      className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs"
                    >
                      Dismantle
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <EnableDisablePppModal
          isOpen={openModal}
          row={selectedRow}
          onClose={() => setOpenModal(false)}
          onSuccess={() => fetchPppSecrets()}
        />

        <AddWfhPppSecretModal
          isOpen={openAddModal}
          onClose={() => setOpenAddModal(false)}
          usermanId={usermanId}
          onSuccess={() => {
            setPage(1);
            fetchPppSecrets();
            setOpenAddModal(false);
          }}
        />

        <EditPppSecretModal
          isOpen={openEditModal}
          data={selectedRow}
          usermanId={usermanId}
          onClose={() => {
            setOpenEditModal(false);
            setSelectedRow(null);
          }}
          onSuccess={() => {
            fetchPppSecrets();
            setOpenEditModal(false);
            setSelectedRow(null);
          }}
        />

        <DeletePppSecretModal
          isOpen={openDeleteModal}
          row={selectedRow}
          usermanId={usermanId}
          onClose={() => {
            setOpenDeleteModal(false);
            setSelectedRow(null);
          }}
          onSuccess={() => {
            fetchPppSecrets();
            setOpenDeleteModal(false);
            setSelectedRow(null);
          }}
        />
      </div>

      {/* PAGINATION */}
      <div className="mt-4 flex items-center gap-2">
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1 rounded border dark:border-slate-700 disabled:opacity-50"
        >
          Prev
        </button>

        <span>
          Page {page} / {totalPages}
        </span>

        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 rounded border dark:border-slate-700 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PppSecrets;
